import { SiteLayout } from "@/components/layout/SiteLayout";
import { SEO } from "@/components/SEO";
import { Link } from "wouter";
import { breadcrumbSchema, webPageSchema } from "@/lib/seo";

export default function Corrections() {
  const page = webPageSchema({
    path: "/corrections",
    title: "Corrections & Updates Policy | Shariq Refai, MD, MBA",
    description:
      "How factual errors are corrected, how content is updated as evidence evolves, and how readers can report an issue with an essay or page on shariqrefai.com.",
    breadcrumbs: [
      { name: "Home", path: "/" },
      { name: "Corrections", path: "/corrections" },
    ],
  });
  return (
    <SiteLayout>
      <SEO
        title="Corrections & Updates Policy | Shariq Refai, MD, MBA"
        description="How factual errors are corrected, how content is updated as evidence evolves, and how readers can report an issue with an essay or page on shariqrefai.com."
        url="/corrections"
        schema={[
          page,
          breadcrumbSchema([
            { name: "Home", path: "/" },
            { name: "Corrections", path: "/corrections" },
          ]),
        ]}
      />

      <header className="pt-20 pb-12 px-4 md:px-8 bg-muted/30 border-b border-border/50">
        <div className="container mx-auto max-w-3xl">
          <nav
            aria-label="Breadcrumb"
            className="mb-6 text-xs tracking-widest uppercase text-muted-foreground"
          >
            <Link href="/" className="hover:text-accent transition-colors">
              Home
            </Link>
            <span className="mx-2" aria-hidden="true">/</span>
            <span aria-current="page">Corrections</span>
          </nav>
          <h1 className="text-4xl md:text-5xl font-serif text-primary leading-snug">
            Corrections &amp; Updates Policy
          </h1>
          <p className="mt-4 text-lg text-muted-foreground font-light max-w-2xl">
            How errors are corrected and how content is kept current as
            evidence evolves.
          </p>
        </div>
      </header>

      <article className="py-16 md:py-20 px-4 md:px-8">
        <div className="container mx-auto max-w-3xl prose prose-lg max-w-none prose-headings:font-serif prose-headings:font-normal prose-headings:text-primary prose-h2:text-2xl prose-h2:mt-12 prose-h2:mb-4 prose-h3:text-xl prose-h3:mt-8 prose-h3:mb-3 prose-p:font-light prose-p:text-secondary prose-p:leading-relaxed prose-a:text-accent hover:prose-a:text-primary prose-strong:text-primary">
          <p>
            <strong>Last updated: May 23, 2026.</strong>
          </p>

          <p>
            Educational content about psychiatry, mental health, and human
            behavior changes over time. Evidence evolves, terminology
            shifts, and individual essays can have errors. This page
            describes how shariqrefai.com handles all of that.
          </p>

          <h2>Factual Correction Process</h2>
          <p>
            When a factual error is identified, whether by Dr. Refai, the
            editorial process, or a reader, the response is:
          </p>
          <ol>
            <li>Verify the claim against primary sources or current practice guidelines.</li>
            <li>Update the affected page with the correct information.</li>
            <li>Add a brief correction note to the page itself when the change is material.</li>
            <li>Update the "Reviewed" or "Updated" date at the top or bottom of the page.</li>
          </ol>
          <p>
            Minor typo fixes, link repairs, and formatting cleanups are made
            silently and do not require a correction note.
          </p>

          <h2>Evidence Update Process</h2>
          <p>
            When clinical evidence shifts, for example, a new
            meta-analysis, an updated practice guideline, a label change,
            or a major change in expert consensus, affected content is
            reviewed and revised. Significant evidence updates are noted on
            the page so readers can see what changed and why.
          </p>
          <p>
            See <Link href="/evidence">How We Evaluate Evidence</Link> for
            the evidence hierarchy used to make those judgments.
          </p>

          <h2>Publication Update Process</h2>
          <p>
            Long-form essays and reference pages are reviewed on a recurring
            basis as part of editorial maintenance, even when no specific
            error has been flagged. Updates can include:
          </p>
          <ul>
            <li>Refreshed citations and sources.</li>
            <li>Tightened language to better reflect current consensus.</li>
            <li>New disclaimers or context where helpful.</li>
            <li>Removal of content that's no longer accurate or useful.</li>
          </ul>

          <h2>Versioning and Dates</h2>
          <p>Material editorial pages may include these labels:</p>
          <ul>
            <li><strong>Published:</strong> the date the page first went live.</li>
            <li><strong>Reviewed:</strong> the most recent date the page was reviewed by Dr. Refai or the editorial process.</li>
            <li><strong>Sources checked:</strong> the most recent date primary sources cited on the page were re-verified.</li>
            <li><strong>Updated:</strong> the most recent date the page was substantively edited.</li>
          </ul>
          <p>
            Not every page carries all four labels. Legal and policy pages
            use a single "Last updated" line.
          </p>

          <h2>How to Report an Issue</h2>
          <p>
            If you've spotted an error, a confusing claim, an outdated
            recommendation, or a broken link, please tell us. The fastest
            path is:
          </p>
          <ol>
            <li>Email{" "}
              <a href="mailto:corrections@shariqrefai.com">
                corrections@shariqrefai.com
              </a>{" "}
              with the subject line "Correction request".
            </li>
            <li>Include the page URL, the specific quote or claim, and what you believe the issue is.</li>
            <li>Where possible, link to a primary source supporting the correction (a study, guideline, or authoritative reference).</li>
          </ol>
          <p>
            You can also use the <Link href="/contact">contact page</Link>{" "}
            and select an appropriate inquiry category. Reports are reviewed
            in the order received. Substantive corrections are typically
            acted on within a reasonable window once verified; we don't
            promise a specific turnaround for any individual request.
          </p>

          <h2>What We Don't Do</h2>
          <ul>
            <li>We do not provide diagnostic, treatment, or medication advice in response to corrections inquiries.</li>
            <li>We do not remove substantive editorial perspectives because a reader disagrees with the conclusion; that's an editorial difference, not a factual error.</li>
            <li>We do not handle clinical complaints about shrinkMD, shrinQ, Unstuck, or any other organization through this corrections process. Each of those has its own contact channels.</li>
          </ul>

          <h2>Crisis Resources</h2>
          <p>
            If you are in crisis or feel unsafe, this is not the right
            channel. Call or text <a href="tel:988">988</a> in the United
            States, call <a href="tel:911">911</a>, or go to the nearest
            emergency room. Outside the United States, contact your local
            emergency services.
          </p>

          <h2>Related</h2>
          <p>
            See the <Link href="/editorial-process">Editorial Process</Link>{" "}
            page for how content is reviewed in the first place, and the{" "}
            <Link href="/evidence">Evidence Methodology</Link> page for how
            evidence is weighed.
          </p>
        </div>
      </article>
    </SiteLayout>
  );
}
